import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CreditCard, Clock, CheckCircle2, Users } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ClassService } from "@/services/classService";
import { enrollmentTransactionService } from "@/services/enrollment/EnrollmentTransactionService";
import { centsToDollars } from "@/utils/money";
import { useAuth } from "@/hooks/useAuth";

interface Class {
  id: string;
  name: string;
  price_cents: number;
  billing_model: string;
  payment_policy: 'immediate' | 'flexible';
  payment_deadline_days: number;
  max_students?: number;
}

interface EnrollmentWithPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classData: Class;
  organizationId: string;
  onEnrollmentSuccess: () => void;
  currentEnrollmentCount: number;
}

const EnrollmentWithPaymentDialog: React.FC<EnrollmentWithPaymentDialogProps> = ({
  open,
  onOpenChange,
  classData,
  organizationId,
  onEnrollmentSuccess,
  currentEnrollmentCount
}) => {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState<any[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<string>("");
  const [enrollingStatus, setEnrollingStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [enableAutoBilling, setEnableAutoBilling] = useState(false);
  const [billingDay, setBillingDay] = useState(15);
  const { user } = useAuth();
  
  const isAtCapacity = classData.max_students !== undefined && currentEnrollmentCount >= classData.max_students;
  const requiresPayment = classData.payment_policy === 'immediate' && classData.price_cents > 0;
  const isRecurringBilling = classData.billing_model === 'monthly' || classData.billing_model === 'weekly';
  
  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);
      try {
        const availableStudents = await ClassService.getStudentsNotEnrolledInClass(classData.id);
        setStudents(availableStudents);
      } catch (error) {
        console.error("Error fetching students:", error);
        toast.error("Failed to load available students");
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchStudents();
    } else {
      // Reset state when dialog closes
      setSelectedStudent("");
      setEnrollingStatus("idle");
      setEnableAutoBilling(false);
      setBillingDay(15);
    }
  }, [open, classData.id]);

  const handleEnrollStudent = async () => {
    if (!selectedStudent) {
      toast.error("Please select a student to enroll");
      return;
    }

    setEnrollingStatus("loading");
    
    try {
      // Use EnrollmentTransactionService for atomic enrollment + payment
      const transactionRequest = {
        student_id: selectedStudent,
        class_id: classData.id,
        organization_id: organizationId,
        enrolled_by: user?.id || '',
        auto_billing_enabled: enableAutoBilling && isRecurringBilling,
        billing_amount_cents: enableAutoBilling ? classData.price_cents : undefined,
        billing_day: enableAutoBilling ? billingDay : undefined,
        payment_strategy: classData.payment_policy as 'immediate' | 'deferred',
        payment_window_hours: classData.payment_policy === 'flexible' ? classData.payment_deadline_days * 24 : 2
      };

      // Create transaction
      const transaction = await enrollmentTransactionService.createTransaction(transactionRequest);
      
      // Execute transaction
      const result = await enrollmentTransactionService.executeTransaction(transaction.id);

      if (result.success) {
        let successMessage = "";
        let description = "";

        if (result.enrollment_status === 'waitlisted') {
          successMessage = "⏰ Student Successfully Added to Waitlist";
          description = "Student has been added to the waitlist. They will be automatically notified when a spot opens up.";
        } else if (requiresPayment) {
          if (enableAutoBilling) {
            successMessage = "✅ Student Enrolled with Auto-Billing";
            description = "Student has been enrolled and auto-billing has been set up for recurring payments.";
          } else {
            successMessage = "✅ Student Enrolled - Payment Required";
            description = classData.payment_policy === 'immediate' 
              ? "Student enrolled successfully. Payment collection will be initiated."
              : `Student enrolled successfully. Payment is due within ${classData.payment_deadline_days} days.`;
          }
        } else {
          successMessage = "✅ Student Successfully Enrolled";
          description = "Student has been enrolled in the class. The family will receive a confirmation notification.";
        }

        toast.success(successMessage, {
          description,
          duration: 5000,
          action: {
            label: "View Class",
            onClick: () => onOpenChange(false)
          }
        });

        setEnrollingStatus("success");
        onEnrollmentSuccess();
        onOpenChange(false);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error("Error enrolling student:", error);
      toast.error("Failed to enroll student", {
        description: error instanceof Error ? error.message : "Please try again"
      });
      setEnrollingStatus("error");
    }
  };

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (!newOpen && enrollingStatus === "loading") return;
      onOpenChange(newOpen);
    }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isAtCapacity ? (
              <>
                <Users className="h-5 w-5 text-amber-600" />
                Add to Waitlist
              </>
            ) : (
              <>
                <Users className="h-5 w-5 text-blue-600" />
                Enroll Student
              </>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4 space-y-6">
          {/* Class Info */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-2">
            <h4 className="font-medium">{classData.name}</h4>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>Price: {formatPrice(classData.price_cents)}</span>
              <span>•</span>
              <span>Billing: {classData.billing_model}</span>
              <span>•</span>
              <Badge variant={classData.payment_policy === 'immediate' ? 'default' : 'secondary'}>
                {classData.payment_policy === 'immediate' ? 'Payment Required' : 'Flexible Payment'}
              </Badge>
            </div>
          </div>

          {/* Capacity Warning */}
          {isAtCapacity && (
            <Alert variant="warning">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This class is at capacity. The student will be added to the waitlist.
              </AlertDescription>
            </Alert>
          )}

          {/* Payment Policy Info */}
          {requiresPayment && !isAtCapacity && (
            <Alert>
              <CreditCard className="h-4 w-4" />
              <AlertDescription>
                {classData.payment_policy === 'immediate' 
                  ? "Payment will be required to complete enrollment."
                  : `Payment will be due within ${classData.payment_deadline_days} days of enrollment.`
                }
              </AlertDescription>
            </Alert>
          )}
          
          {/* Student Selection */}
          <div className="space-y-2">
            <Label htmlFor="student">
              Select Student 
              {isAtCapacity && <Badge variant="outline" className="ml-2 bg-amber-100 text-amber-800">Will be waitlisted</Badge>}
            </Label>
            <Select
              value={selectedStudent}
              onValueChange={setSelectedStudent}
              disabled={loading || enrollingStatus === "loading"}
            >
              <SelectTrigger>
                <SelectValue placeholder={loading ? "Loading students..." : "Select a student"} />
              </SelectTrigger>
              <SelectContent>
                {loading ? (
                  <SelectItem value="loading" disabled>
                    Loading students...
                  </SelectItem>
                ) : students.length === 0 ? (
                  <SelectItem value="none" disabled>
                    No available students
                  </SelectItem>
                ) : (
                  students.map((student) => (
                    <SelectItem key={student.id} value={student.id}>
                      {student.first_name} {student.last_name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Auto-Billing Option for Recurring Classes */}
          {isRecurringBilling && classData.price_cents > 0 && !isAtCapacity && (
            <div className="space-y-4 p-4 border rounded-lg">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="auto_billing"
                  checked={enableAutoBilling}
                  onCheckedChange={setEnableAutoBilling}
                />
                <div className="space-y-1">
                  <Label htmlFor="auto_billing" className="text-sm font-medium cursor-pointer">
                    Set up automatic billing
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Automatically charge {formatPrice(classData.price_cents)} {classData.billing_model}
                  </p>
                </div>
              </div>

              {enableAutoBilling && (
                <div className="ml-6 space-y-2">
                  <Label htmlFor="billing_day" className="text-sm font-medium">Billing Day of Month</Label>
                  <Select
                    value={billingDay.toString()}
                    onValueChange={(value) => setBillingDay(parseInt(value))}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                        <SelectItem key={day} value={day.toString()}>
                          {day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    The day of each month when payments will be automatically charged
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={enrollingStatus === "loading"}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEnrollStudent}
            disabled={!selectedStudent || enrollingStatus === "loading"}
          >
            {enrollingStatus === "loading" ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                Processing...
              </>
            ) : isAtCapacity ? (
              "Add to Waitlist"
            ) : requiresPayment ? (
              "Enroll & Process Payment"
            ) : (
              "Enroll Student"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EnrollmentWithPaymentDialog;